import React from "react";
import { FOOTER_DATA } from "../data/mock";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from "lucide-react";
import { Separator } from "./ui/separator";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const Footer = () => {
  const { t, i18n } = useTranslation();
  return (
    <footer className="bg-[#0A1929] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Column 1 - About */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[#C8A24D] flex items-center justify-center">
                <span className="text-[#0C2340] font-bold text-sm" style={{ fontFamily: "'DM Serif Display', serif" }}>R</span>
              </div>
              <div>
                <span className="text-white text-base font-semibold" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  ResidenciaYA
                </span>
                <span className="text-[10px] text-[#C8A24D] font-medium tracking-[0.15em]">.es</span>
              </div>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              {t("footer.about")}
            </p>
            <div className="flex items-center gap-4">
              <a
                href={FOOTER_DATA.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#C8A24D] hover:text-[#0C2340] transition-all duration-300 border border-white/10"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={FOOTER_DATA.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#C8A24D] hover:text-[#0C2340] transition-all duration-300 border border-white/10"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2 - Legal */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t("footer.legalTitle")}</h3>
            <ul className="space-y-3">
              {FOOTER_DATA.legal.map((item, idx) => (
                <li key={item.label}>
                  <Link
                    to={`/${i18n.language}${item.href}`}
                    className="text-white/50 text-sm hover:text-[#C8A24D] transition-colors duration-200"
                  >
                    {t(`footer.link${idx + 1}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Contact */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t("footer.contactTitle")}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C8A24D] shrink-0 mt-0.5" />
                <span className="text-white/50 text-sm leading-relaxed">{t("footer.address")}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#C8A24D] shrink-0" />
                <a href={`tel:${t("footer.phone")}`} className="text-white/50 text-sm hover:text-[#C8A24D] transition-colors">
                  {t("footer.phone")}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C8A24D] shrink-0" />
                <a href={`mailto:${t("footer.email")}`} className="text-white/50 text-sm hover:text-[#C8A24D] transition-colors">
                  {t("footer.email")}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Hours */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t("footer.hoursTitle")}</h3>
            <ul className="space-y-3">
              {FOOTER_DATA.hours.map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#C8A24D] shrink-0" />
                  <div>
                    <span className="text-white/70 text-sm font-medium">{t(`footer.day${idx + 1}`)}</span>
                    <span className="text-white/40 text-sm ml-2">{t(`footer.time${idx + 1}`)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-10 bg-white/10" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} ResidenciaYA.es — {t("footer.copyright")}
          </p>
          <p className="text-white/30 text-xs">
            {t("footer.firm")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
